import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';
import { Loader2, CheckCircle, AlertCircle, Landmark, TrendingDown, Globe, Search } from 'lucide-react';
import { symFor } from '../hooks/useCurrency';
import { PinModal } from '../components/PinModal';
import { useNavigate } from 'react-router-dom';

const Withdraw = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [selectedBank, setSelectedBank] = useState(null);
  const [manualBankName, setManualBankName] = useState('');
  const [manualBankCode, setManualBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [step, setStep] = useState(1);
  const [showPin, setShowPin] = useState(false);
  const [pinLoading, setPinLoading] = useState(false); // 1=country, 2=bank+account, 3=amount+confirm

  const loadWallet = useCallback(async () => {
    try {
      const res = await api.get('/wallet');
      setWallet(res.data);
    } catch (error) {
      toast.error('Failed to load wallet');
    }
  }, []);

  const loadCountries = useCallback(async () => {
    try {
      const res = await api.get('/payout-countries');
      setCountries(res.data.countries || []);
    } catch (error) {
      toast.error('Failed to load payout countries');
    }
  }, []);

  const loadWithdrawals = useCallback(async () => {
    try {
      const res = await api.get('/withdrawals');
      setWithdrawals(res.data.withdrawals || []);
    } catch (error) {
      toast.error('Failed to load withdrawal history');
    }
  }, []);

  const loadSavedAccounts = useCallback(async () => {
    try {
      const res = await api.get('/withdrawals/bank-accounts');
      setSavedAccounts(res.data.bank_accounts || []);
    } catch (error) {
      toast.error('Failed to load saved accounts');
    }
  }, []);

  useEffect(() => {
    loadWallet();
    loadCountries();
    loadWithdrawals();
    loadSavedAccounts();
  }, [loadWallet, loadCountries, loadWithdrawals, loadSavedAccounts]);

  const handleCountrySelect = async (code) => {
    setSelectedCountry(code);
    setSelectedBank(null);
    setManualBankName('');
    setManualBankCode('');
    setAccountNumber('');
    setAccountName('');
    setVerified(false);
    setBankSearch('');
    setBanksLoading(true);
    try {
      const res = await api.get(`/banks/${code}`);
      setBanks(res.data.banks || []);
    } catch (error) {
      setBanks([]);
      toast.error('Failed to load banks for this country');
    }
    setBanksLoading(false);
    setStep(2);
  };

  const isManualEntry = banks.length === 0 && selectedCountry;

  const handleVerifyAccount = async () => {
    const bankCode = selectedBank?.code || manualBankCode;
    if (!accountNumber || !bankCode) {
      toast.error('Please fill in bank and account details');
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post(`/withdrawals/verify-account?account_number=${accountNumber}&account_bank=${bankCode}&country=${selectedCountry}`);
      if (res.data.success) {
        setAccountName(res.data.account_name);
        setVerified(true);
        toast.success(`Account verified: ${res.data.account_name}`);
      } else {
        toast.error(res.data.error || 'Verification failed');
      }
    } catch (err) {
      // For countries without resolve support, allow manual entry
      toast.info('Auto-verification not available for this country. Please enter account holder name manually.');
      setVerified(true);
    }
    setVerifying(false);
  };

  const proceedToAmount = () => {
    const bankCode = selectedBank?.code || manualBankCode;
    if (!bankCode || !accountNumber || !accountName) {
      toast.error('Please complete all bank details');
      return;
    }
    setStep(3);
  };

  const handleWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt < 1) {
      toast.error(`Minimum withdrawal is ${symFor(wallet?.currency)}1`);
      return;
    }
    if (amt > (wallet?.balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }
    // Check PIN
    try {
      const pinCheck = await api.get('/auth/has-pin');
      if (!pinCheck.data.has_pin) {
        toast.error('Please set a transaction PIN first');
        navigate('/profile');
        return;
      }
    } catch {
      toast.error('Could not verify PIN status');
      return;
    }
    setShowPin(true);
  };

  const handlePinSubmitWithdraw = async (pin) => {
    const bankCode = selectedBank?.code || manualBankCode;
    const bName = selectedBank?.name || manualBankName;

    setPinLoading(true);
    try {
      const res = await api.post(
        `/withdrawals/initiate?account_number=${encodeURIComponent(accountNumber)}&bank_code=${encodeURIComponent(bankCode)}&bank_name=${encodeURIComponent(bName)}&account_name=${encodeURIComponent(accountName)}&amount=${parseFloat(withdrawAmount)}&transaction_pin=${encodeURIComponent(pin)}&country=${selectedCountry}`
      );
      if (res.data.success) {
        toast.success('Withdrawal initiated successfully!');
        try {
          await api.post(
            `/withdrawals/add-bank-account?account_number=${encodeURIComponent(accountNumber)}&bank_code=${encodeURIComponent(bankCode)}&bank_name=${encodeURIComponent(bName)}&account_name=${encodeURIComponent(accountName)}&country=${selectedCountry}`
          );
        } catch {
          // Bank account save is best-effort
        }
        setShowPin(false);
        resetForm();
        loadWallet();
        loadWithdrawals();
        loadSavedAccounts();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Withdrawal failed';
      toast.error(detail);
      if (!detail.includes('Invalid transaction PIN')) {
        setShowPin(false);
      }
    }
    setPinLoading(false);
  };

  const selectSavedAccount = (acc) => {
    const country = acc.country || 'NG';
    setSelectedCountry(country);
    setBanksLoading(true);
    api.get(`/banks/${country}`).then(res => {
      setBanks(res.data.banks || []);
    }).catch(() => {
      setBanks([]);
    }).finally(() => {
      setBanksLoading(false);
      setAccountNumber(acc.account_number);
      setAccountName(acc.account_name);
      setManualBankCode(acc.bank_code);
      setManualBankName(acc.bank_name);
      setVerified(true);
      setShowSaved(false);
      setStep(3);
    });
  };

  const resetForm = () => {
    setStep(1);
    setSelectedCountry('');
    setSelectedBank(null);
    setManualBankName('');
    setManualBankCode('');
    setAccountNumber('');
    setAccountName('');
    setVerified(false);
    setWithdrawAmount('');
    setBankSearch('');
    setBanks([]);
  };

  const filteredBanks = banks.filter(b =>
    b.name?.toLowerCase().includes(bankSearch.toLowerCase())
  );

  const currentCountry = countries.find(c => c.code === selectedCountry);
  const payoutCurrency = currentCountry?.currency || '';
  const wSym = symFor(wallet?.currency);

  const statusColor = (s) => {
    if (!s) return 'bg-gray-100 text-gray-700';
    const sl = s.toLowerCase();
    if (sl === 'completed' || sl === 'successful') return 'bg-green-100 text-green-700';
    if (sl === 'failed' || sl === 'reversed') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="withdraw-title">Withdraw Funds</h1>
            <p className="text-sm text-muted-foreground">
              Send money to bank accounts worldwide via Flutterwave
            </p>
          </div>
          {wallet && (
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Available Balance</p>
              <p className="text-2xl font-bold text-primary" data-testid="withdraw-balance">
                {wSym}{wallet.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
          )}
        </div>

        {/* Saved Accounts Quick Access */}
        {savedAccounts.length > 0 && (
          <Card className="border shadow-sm">
            <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowSaved(!showSaved)}>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2"><Landmark className="h-4 w-4" /> Saved Bank Accounts ({savedAccounts.length})</span>
                <span className="text-xs text-muted-foreground">{showSaved ? 'Hide' : 'Show'}</span>
              </CardTitle>
            </CardHeader>
            {showSaved && (
              <CardContent className="pt-0">
                <div className="grid gap-2">
                  {savedAccounts.map((acc, i) => (
                    <button
                      key={`${acc.bank_code}-${acc.account_number}`}
                      onClick={() => selectSavedAccount(acc)}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left w-full"
                      data-testid={`saved-account-${i}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{acc.account_name}</p>
                        <p className="text-xs text-muted-foreground">{acc.bank_name} - {acc.account_number}</p>
                      </div>
                      <span className="text-xs bg-muted px-2 py-1 rounded">{acc.country || 'NG'}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Withdrawal Form */}
        <Card className="border shadow-sm" data-testid="withdraw-form-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" /> New Withdrawal
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Select the destination country'}
              {step === 2 && `Enter bank details for ${currentCountry?.name || ''}`}
              {step === 3 && 'Confirm withdrawal amount'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Step 1: Country Selection */}
            {step >= 1 && (
              <div className="space-y-2">
                <Label>Destination Country</Label>
                <Select
                  value={selectedCountry}
                  onValueChange={(val) => handleCountrySelect(val)}
                  data-testid="country-select"
                >
                  <SelectTrigger data-testid="country-select-trigger">
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`country-option-${c.code}`}>
                        {c.name} ({c.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 2: Bank & Account */}
            {step >= 2 && selectedCountry && (
              <div className="space-y-4 pt-2 border-t">
                {banksLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading banks...
                  </div>
                ) : isManualEntry ? (
                  /* Manual entry for countries without bank list (UK, EU, etc.) */
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
                      Enter your bank details manually for {currentCountry?.name} payouts
                    </p>
                    <div>
                      <Label>Bank / Institution Name</Label>
                      <Input
                        placeholder="e.g. Barclays, Chase, Deutsche Bank"
                        value={manualBankName}
                        onChange={(e) => setManualBankName(e.target.value)}
                        data-testid="manual-bank-name"
                      />
                    </div>
                    <div>
                      <Label>Bank/Routing Code (Sort Code, SWIFT, etc.)</Label>
                      <Input
                        placeholder="e.g. 12-34-56 or BARCGB22"
                        value={manualBankCode}
                        onChange={(e) => setManualBankCode(e.target.value)}
                        data-testid="manual-bank-code"
                      />
                    </div>
                    <div>
                      <Label>Account Number / IBAN</Label>
                      <Input
                        placeholder="Enter account number or IBAN"
                        value={accountNumber}
                        onChange={(e) => { setAccountNumber(e.target.value); setVerified(false); }}
                        data-testid="account-number-input"
                      />
                    </div>
                    <div>
                      <Label>Account Holder Name</Label>
                      <Input
                        placeholder="Full name on the account"
                        value={accountName}
                        onChange={(e) => { setAccountName(e.target.value); setVerified(true); }}
                        data-testid="account-name-input"
                      />
                    </div>
                  </div>
                ) : (
                  /* Bank selection from list */
                  <div className="space-y-3">
                    <div>
                      <Label>Search Bank</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          className="pl-9"
                          placeholder={`Search ${banks.length} banks...`}
                          value={bankSearch}
                          onChange={(e) => setBankSearch(e.target.value)}
                          data-testid="bank-search-input"
                        />
                      </div>
                      {bankSearch && (
                        <div className="mt-1 max-h-48 overflow-y-auto border rounded-lg bg-background">
                          {filteredBanks.slice(0, 30).map((bank) => (
                            <button
                              key={bank.code}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${selectedBank?.code === bank.code ? 'bg-primary/10 font-medium' : ''}`}
                              onClick={() => {
                                setSelectedBank(bank);
                                setBankSearch(bank.name);
                                setVerified(false);
                              }}
                              data-testid={`bank-option-${bank.code}`}
                            >
                              {bank.name}
                            </button>
                          ))}
                          {filteredBanks.length === 0 && (
                            <p className="px-3 py-2 text-sm text-muted-foreground">No banks found</p>
                          )}
                        </div>
                      )}
                      {selectedBank && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Selected: {selectedBank.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input
                        placeholder="Enter account number"
                        value={accountNumber}
                        onChange={(e) => { setAccountNumber(e.target.value); setVerified(false); }}
                        data-testid="account-number-input"
                      />
                    </div>
                    {!verified && accountNumber && selectedBank && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleVerifyAccount}
                        disabled={verifying}
                        data-testid="verify-account-btn"
                      >
                        {verifying ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Verifying...</> : 'Verify Account'}
                      </Button>
                    )}
                    {verified && accountName && (
                      <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                        <CheckCircle className="h-4 w-4" />
                        <span data-testid="verified-name">{accountName}</span>
                      </div>
                    )}
                  </div>
                )}

                {step === 2 && verified && accountName && (
                  <Button onClick={proceedToAmount} className="w-full" data-testid="proceed-to-amount-btn">
                    Continue to Amount
                  </Button>
                )}
              </div>
            )}

            {/* Step 3: Amount & Confirm */}
            {step === 3 && (
              <div className="space-y-4 pt-2 border-t">
                <div className="bg-muted rounded-lg p-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Destination</span>
                    <span className="font-medium">{currentCountry?.name} ({payoutCurrency})</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Bank</span>
                    <span className="font-medium">{selectedBank?.name || manualBankName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account</span>
                    <span className="font-medium">{accountNumber} — {accountName}</span>
                  </div>
                </div>

                <div>
                  <Label>Withdrawal Amount ({wSym})</Label>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    data-testid="withdraw-amount-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Available: {wSym}{wallet?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                    Back
                  </Button>
                  <Button
                    onClick={handleWithdraw}
                    disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) < 1}
                    className="flex-1"
                    data-testid="confirm-withdraw-btn"
                  >
                    {withdrawing ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                    ) : (
                      <>Withdraw {wSym}{parseFloat(withdrawAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Withdrawal History */}
        {withdrawals.length > 0 && (
          <Card className="border shadow-sm" data-testid="withdrawal-history">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-primary" /> Withdrawal History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {withdrawals.map((w, i) => (
                  <div key={w.transfer_reference || i} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`withdrawal-item-${i}`}>
                    <div>
                      <p className="font-medium text-sm">{w.bank_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {w.account_number} {w.country ? `(${w.country})` : ''} — {new Date(w.initiated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-sm text-red-600">
                        -{wSym}{w.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusColor(w.status)}`}>
                        {w.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <PinModal
          open={showPin}
          onClose={() => setShowPin(false)}
          onSubmit={handlePinSubmitWithdraw}
          loading={pinLoading}
        />
      </div>
    </div>
  );
};

export default Withdraw;
